'use client';
import { MultiServicePreset } from '@/types';

import Button from '@/components/Button';
import CustomLink from '@/components/CustomLink';
import DipHorizontalGradient from '@/components/DipHorizontalGradient';
import DipVerticalGradient from '@/components/DipVerticalGradient';
import SectionTitle from '@/components/SectionTitle';
import Subheader from '@/components/Subheader';
import SimpleText from '@/components/SimpleText';
import { useConfig } from '@/app/contexts/ConfigContext';
import { URL_S3 } from '@/helpers/constants';
import { SERVICE_TYPES } from '@/helpers/enums';
import { ServiceType } from '@/types';

type ResourcesCardAsset = {
  imageServiceType: ServiceType;
};

export default function ResourcesSectionMulti() {
  const { wording, config, setConfig, updateWording, selectedLanguage } =
    useConfig();
  const resourcesConfig = config.multiService === MultiServicePreset.AML
    ? {
        cardOne: {
          imageServiceType: SERVICE_TYPES.MEDITATION,
        },
        cardTwo: {
          imageServiceType: SERVICE_TYPES.LOVE,
        },
        highlight: {
          imageServiceType: SERVICE_TYPES.ASTROLOGY,
        },
        ctaHref: '/media/media_all',
        ctaServiceType: SERVICE_TYPES.ASTROLOGY,
      }
    : {
        cardOne: {
          imageServiceType: SERVICE_TYPES.YOGA,
        },
        cardTwo: {
          imageServiceType: SERVICE_TYPES.PILATES,
        },
        highlight: {
          imageServiceType: SERVICE_TYPES.FITNESS,
        },
        ctaHref:
          config.serviceType === SERVICE_TYPES.FITNESS
            ? '/media/media_1'
            : '/media/media_all',
        ctaServiceType: config.serviceType,
      };

  const handleConfig = (type: ServiceType) => {
    setConfig({
      ...config,
      serviceType: type,
    });
    updateWording(type, selectedLanguage);
  };

  return (
    <section className='xl:grid xl:grid-cols-2 xl:gap-x-20 xl:mt-16 mt-8 xl:mb-32 mb-8'>
      <SectionTitle className='xl:col-span-2'>
        {wording?.components_ressources_section_title}
      </SectionTitle>
      <div className='flex flex-col'>
        <Subheader className='mt-4 sm:mb-8 mb-3'>
          {wording?.components_ressources_subheader_text}
        </Subheader>
        <span
          className='text-neutral-500 sm:text-xl text-sm font-medium sm:leading-[29px] leading-tight'
          style={{ color: config.theme.textColorDarkSecondary }}
        >
          {wording?.components_ressources_span_text}
        </span>
        <div className='xl:mt-28 mt-5 mb-4 grid grid-cols-1 gap-3 xl:block z-10'>
          <div
            className='flex sm:flex-row flex-col sm:gap-10 gap-4 items-center justify-between sm:p-8 py-4 px-3 bg-zinc-100'
            style={{ backgroundColor: config.theme.backgroundColorSecondary }}
          >
            <div className='sm:ml-[15px]'>
              <h3
                className='mb-3 text-neutral-800 sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
                style={{ color: config.theme.textColorDark }}
              >
                {wording?.components_ressources_card_1_title}
              </h3>
              <SimpleText>
                {wording?.components_ressources_card_1_text}
              </SimpleText>
            </div>
            <img
              src={`${URL_S3}/${resourcesConfig.cardOne.imageServiceType}/images/resources-1.webp`}
              alt='image'
            />
          </div>
          <div
            className='xl:mt-5 flex sm:flex-row flex-col sm:gap-10 gap-4 items-center justify-between sm:p-8 py-4 px-3 bg-zinc-100'
            style={{ backgroundColor: config.theme.backgroundColorSecondary }}
          >
            <div>
              <h3
                className='mb-3 text-neutral-800 sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
                style={{ color: config.theme.textColorDark }}
              >
                {wording?.components_ressources_card_2_title}
              </h3>
              <SimpleText>
                {wording?.components_ressources_card_2_text}
              </SimpleText>
            </div>
            <img
              src={`${URL_S3}/${resourcesConfig.cardTwo.imageServiceType}/images/resources-1.webp`}
              alt='image'
            />
          </div>
        </div>
      </div>
      <div className='relative isolate'>
        <div
          className='absolute xl:w-[645px] xl:h-[645px] sm:w-[645px] sm:h-[645px] w-[270px] h-[270px] bottom-0 top-0 -right-12 xl:right-0 xl:left-auto xl:translate-x-0 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:m-0 m-auto bg-gradient-to-b from-fuchsia-300 to-indigo-200 rounded-full z-[1]'
          style={
            config.theme.gradientColorStart && config.theme.gradientColorEnd
              ? {
                  backgroundImage: `linear-gradient(to bottom, ${config.theme.gradientColorStart}, ${config.theme.gradientColorEnd})`,
                }
              : {}
          }
        />
        <div className='relative z-10 flex items-center justify-center h-full'>
          <div className='relative isolate'>
            <img
              className='hidden sm:block relative'
              src={`${URL_S3}/${resourcesConfig.highlight.imageServiceType}/images/resources-3.webp`}
              alt='image'
            />
            <DipVerticalGradient
              className='hidden sm:block absolute top-0 left-0 opacity-50'
              colorStart={config.theme.gradientColorStart}
              colorEnd={config.theme.gradientColorEnd}
            />
            <img
              className='block sm:hidden relative'
              src={`${URL_S3}/${resourcesConfig.highlight.imageServiceType}/images/resources-3-mobile.webp`}
              alt='image'
              style={{ width: 'calc(100% - 1px)' }}
            />
            <DipHorizontalGradient
              className='block sm:hidden absolute top-0 left-0 opacity-50 w-full h-auto max-w-[390px] max-h-[189px]'
              colorStart={config.theme.gradientColorStart}
              colorEnd={config.theme.gradientColorEnd}
            />
            <Button
              className='absolute left-4 bottom-5'
              themeBackgroundColor={config.theme.accentColorOne}
              themeTextColor={config.theme.textColorLight}
            >
              <CustomLink
                href={resourcesConfig.ctaHref}
                onClick={() => {
                  handleConfig(resourcesConfig.ctaServiceType);
                }}
              >
                {wording?.components_ressources_link}
              </CustomLink>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
